'use client';

import 'BomdisoftComponents/page-layout/styles.css';
import { InfoTip, type InfoTipSize } from 'BomdisoftComponents/info-tip/index';
import { PageHeading } from 'BomdisoftComponents/page-heading/index';
import { TabbedBody } from 'BomdisoftComponents/page-layout/tabbed-body';
import type { PersistedHandle } from '@bomdisoft/store/persisted/types';
import type { ReactNode } from 'react';

export type PageLayoutInfo = {
  /** The prose the tip opens with. */
  body: string;
  /** Term and description pairs: a table page explains its columns, a tabbed page its tabs. */
  entries?: readonly { description: string; term: ReactNode }[];
  size?: InfoTipSize;
  title: string;
};

type PageLayoutTabsBase<Value extends string> = {
  /** What stands under the tab row and above every panel: an error alert, a
   *  loading indicator, a filter the page applies across all of its tabs. It
   *  keeps its state when the reader switches tab, which content folded into
   *  each panel would not. */
  common?: ReactNode;
  items: readonly { label: ReactNode; panel: ReactNode; value: Value }[];
  /** Names the tablist for a screen reader. */
  label: string;
};

/**
 * Where the active tab lives.
 *
 * `handle` is the usual one: a `persisted` handle the screen declares, so the
 * tab rides the address and the reader's storage. `value` is for a screen whose
 * tab is already held somewhere else, a reducer for instance, where minting a
 * second source of truth would let the two disagree.
 */
export type PageLayoutTabs<Value extends string = string> = PageLayoutTabsBase<Value> &
  (
    | {
        handle: PersistedHandle<Value>;
        /** Run after the handle is written, for state a screen drops when the
         *  reader moves tab: a dialog belonging to the tab being left. */
        onChange?: (value: Value) => void;
        value?: never;
      }
    | { handle?: never; onChange: (value: Value) => void; value: Value }
  );

type PageLayoutBase = {
  /** Buttons beside the title. */
  actions?: ReactNode;
  crumbs?: ReactNode;
  /** View and layout pickers, which sit on the title's line. */
  controls?: ReactNode;
  /** The prose line under the title saying what the page is. */
  description: ReactNode;
  info: PageLayoutInfo;
  /** `console` is the full width every console page uses. `reading` is the narrower
   *  column a document wants. */
  measure?: 'console' | 'reading';
  testId: string;
  title: ReactNode;
};

/** A page carries a body OR a set of tab panels, never both and never neither. */
export type PageLayoutProps<Value extends string = string> = PageLayoutBase & ({ children: ReactNode; tabs?: never } | { children?: never; tabs: PageLayoutTabs<Value> });

/**
 * The shape every page in every console and admin app carries: a title, the line
 * under it saying what the page is, an info tip, optionally actions or layout
 * controls on the right, and a separator between the head and the content.
 *
 * It renders the head itself, deriving the separator and the centring from
 * what the caller actually passed. Both were rules a caller had to remember and
 * a spec had to police; here they are consequences of the props, so a page
 * cannot express the wrong one.
 */
export const PageLayout = <Value extends string>({ actions, children, controls, crumbs, description, info, measure = 'console', tabs, testId, title }: PageLayoutProps<Value>): ReactNode => {
  const side = controls ?? actions;

  return (
    <div className='bomdisoft-page-layout-frame' data-measure={measure} data-page-layout='' data-testid={testId}>
      <div className='bomdisoft-page-layout-page-head' data-align={controls === undefined ? undefined : 'center'} data-divider={tabs === undefined ? '' : undefined} data-testid={`${testId}-head`}>
        <div className='bomdisoft-page-layout-titles'>
          {crumbs === undefined || crumbs === null ? null : (
            <div className='bomdisoft-page-layout-crumbs' data-testid={`${testId}-crumbs`}>
              {crumbs}
            </div>
          )}
          <PageHeading className='bomdisoft-page-layout-page-title' testId={`${testId}-title`}>
            {title}
          </PageHeading>
          <div className='bomdisoft-page-layout-page-notice'>
            {description}
            <InfoTip className='bomdisoft-page-layout-head-info' description={info.body} entries={info.entries === undefined ? undefined : [...info.entries]} size={info.size} testId={`${testId}-info`} title={info.title} />
          </div>
        </div>
        {side === undefined || side === null ? null : (
          <>
            <span className='bomdisoft-page-layout-spacer' />
            {side}
          </>
        )}
      </div>
      {tabs === undefined ? (
        <div className='bomdisoft-page-layout-body' data-testid={`${testId}-body`}>
          {children}
        </div>
      ) : (
        <TabbedBody tabs={tabs} testId={testId} />
      )}
    </div>
  );
};
